import React, { useState, useEffect } from 'react';
import { automunityAPI } from '../lib/api';
import './Reports.css';

interface AlertMetrics {
  summary: {
    total_alerts: number;
    acknowledged_count: number;
    resolved_count: number;
    avg_response_minutes: number | null;
    avg_resolution_minutes: number | null;
    min_response_minutes: number | null;
    max_response_minutes: number | null;
  };
  weekly_trend: Array<{
    week_key: string;
    week_start: string;
    alert_count: number;
    resolved_count: number;
    avg_response_minutes: number | null;
    avg_resolution_minutes: number | null;
  }>;
  per_developer: Array<{
    developer_name: string;
    alerts_resolved: number;
    avg_response_minutes: number | null;
    avg_resolution_minutes: number | null;
  }>;
}

interface RequestsPerDev {
  developer_name: string;
  user_id: number;
  total_requests: number;
  completed_requests: number;
  in_progress_requests: number;
  open_requests: number;
  avg_resolution_hours: number | null;
}

interface AutomationsPerDev {
  developer_name: string;
  user_id: number;
  automation_count: number;
  automation_names: string;
}

interface RequestsPerAuto {
  automation_name: string;
  automation_id: number;
  total_requests: number;
  completed_requests: number;
  in_progress_requests: number;
  open_requests: number;
  bug_count: number;
  feature_count: number;
  improvement_count: number;
}

const COLORS = ['#007bff', '#28a745', '#ffc107', '#dc3545', '#6f42c1', '#20c997', '#fd7e14', '#17a2b8'];

const formatMinutes = (minutes: number | null): string => {
  if (minutes === null || minutes === undefined) return '—';
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (hours < 24) return `${hours}h ${mins}m`;
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return `${days}d ${remainingHours}h`;
};

// ─── SVG Chart Components ───────────────────────────────────────────

interface BarChartProps {
  data: Array<{ label: string; values: number[] }>;
  colors: string[];
  legendLabels: string[];
}

const BarChart: React.FC<BarChartProps> = ({ data, colors, legendLabels }) => {
  if (!data.length) return null;
  const maxVal = Math.max(...data.flatMap(d => d.values), 1);
  const chartWidth = 400;
  const chartHeight = 200;
  const padding = { top: 20, right: 20, bottom: 50, left: 40 };
  const plotWidth = chartWidth - padding.left - padding.right;
  const plotHeight = chartHeight - padding.top - padding.bottom;
  const barGroupWidth = plotWidth / data.length;
  const barWidth = barGroupWidth / (colors.length + 1);

  return (
    <div>
      <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="report-chart-svg">
        {/* Y-axis gridlines */}
        {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => (
          <line key={i}
            x1={padding.left} y1={padding.top + plotHeight * (1 - pct)}
            x2={chartWidth - padding.right} y2={padding.top + plotHeight * (1 - pct)}
            stroke="#e9ecef" strokeWidth="1"
          />
        ))}
        {/* Y-axis labels */}
        {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => (
          <text key={i} x={padding.left - 5} y={padding.top + plotHeight * (1 - pct) + 4}
            fontSize="10" fill="#666" textAnchor="end">
            {Math.round(maxVal * pct)}
          </text>
        ))}
        {/* Bars */}
        {data.map((item, idx) => (
          <g key={idx}>
            {item.values.map((val, vIdx) => {
              const barX = padding.left + idx * barGroupWidth + vIdx * barWidth + barWidth * 0.2;
              const barH = (val / maxVal) * plotHeight;
              return (
                <rect key={vIdx} x={barX} y={padding.top + plotHeight - barH}
                  width={barWidth * 0.8} height={barH}
                  fill={colors[vIdx]} rx="2" opacity="0.85"
                />
              );
            })}
            {/* X-axis label */}
            <text x={padding.left + idx * barGroupWidth + barGroupWidth / 2}
              y={chartHeight - padding.bottom + 15}
              fontSize="9" fill="#666" textAnchor="middle">
              {item.label}
            </text>
          </g>
        ))}
      </svg>
      <div className="chart-legend" style={{ flexDirection: 'row', justifyContent: 'center', gap: '20px', marginTop: '8px' }}>
        {legendLabels.map((label, idx) => (
          <div key={idx} className="legend-item" style={{ padding: '4px 0' }}>
            <div className="legend-color" style={{ backgroundColor: colors[idx] }}></div>
            <span className="legend-label">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

interface HorizontalBarChartProps {
  data: Array<{ label: string; value: number }>;
  color: string;
  formatValue: (value: number) => string;
}

const HorizontalBarChart: React.FC<HorizontalBarChartProps> = ({ data, color, formatValue }) => {
  if (!data.length) return null;
  const maxVal = Math.max(...data.map(d => d.value), 1);
  const barHeight = 28;
  const gap = 6;
  const labelWidth = 120;
  const valueWidth = 60;
  const chartWidth = 400;
  const chartHeight = data.length * (barHeight + gap) + 10;
  const barAreaWidth = chartWidth - labelWidth - valueWidth - 20;

  return (
    <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="report-chart-svg"
      style={{ height: `${Math.max(chartHeight, 150)}px` }}>
      {data.map((item, idx) => {
        const y = idx * (barHeight + gap) + 5;
        const barW = (item.value / maxVal) * barAreaWidth;
        return (
          <g key={idx}>
            <text x={labelWidth - 5} y={y + barHeight / 2 + 4}
              fontSize="11" fill="#333" textAnchor="end">
              {item.label.length > 16 ? item.label.slice(0, 14) + '…' : item.label}
            </text>
            <rect x={labelWidth} y={y} width={barW} height={barHeight}
              fill={color} rx="4" opacity="0.8"
            />
            <text x={labelWidth + barW + 6} y={y + barHeight / 2 + 4}
              fontSize="11" fill="#333">
              {formatValue(item.value)}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

// ─── Main Component ─────────────────────────────────────────────────

const Reports: React.FC = () => {
  const [alertMetrics, setAlertMetrics] = useState<AlertMetrics | null>(null);
  const [requestsPerDev, setRequestsPerDev] = useState<RequestsPerDev[]>([]);
  const [automationsPerDev, setAutomationsPerDev] = useState<AutomationsPerDev[]>([]);
  const [requestsPerAuto, setRequestsPerAuto] = useState<RequestsPerAuto[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('90');

  useEffect(() => {
    loadReportData();
  }, [selectedPeriod]);

  const loadReportData = async () => {
    setLoading(true);
    const days = parseInt(selectedPeriod);
    try {
      const [alertsRes, reqDevRes, autoDevRes, reqAutoRes] = await Promise.all([
        automunityAPI.getAlertsResponseMetrics(days),
        automunityAPI.getRequestsPerDeveloper(days),
        automunityAPI.getAutomationsPerDeveloper(),
        automunityAPI.getRequestsPerAutomation(days),
      ]);

      const parseResponse = async (res: any) => {
        const data = await res.body.json();
        if (typeof data === 'string') return JSON.parse(data);
        if (data?.records && typeof data.records === 'string') return JSON.parse(data.records);
        if (Array.isArray(data)) return data;
        return data;
      };

      setAlertMetrics(await parseResponse(alertsRes));
      setRequestsPerDev(await parseResponse(reqDevRes));
      setAutomationsPerDev(await parseResponse(autoDevRes));
      setRequestsPerAuto(await parseResponse(reqAutoRes));
    } catch (error) {
      console.error('Failed to load report data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading reports...</div>;
  }

  const summary = alertMetrics?.summary;
  const weeklyTrend = alertMetrics?.weekly_trend || [];
  const devResponseData = alertMetrics?.per_developer || [];

  return (
    <div className="reports-container">
      <div className="reports-header">
        <h1>📊 Reports</h1>
        <div className="period-selector">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="form-control"
          >
            <option value="30">Last 30 Days</option>
            <option value="90">Last 90 Days</option>
            <option value="180">Last 6 Months</option>
            <option value="365">Last Year</option>
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-stats">
        <div className="summary-card">
          <h3>Total Alerts</h3>
          <div className="summary-value">{summary?.total_alerts ?? 0}</div>
        </div>
        <div className="summary-card">
          <h3>Resolved</h3>
          <div className="summary-value">{summary?.resolved_count ?? 0}</div>
        </div>
        <div className="summary-card">
          <h3>Avg Response Time</h3>
          <div className="summary-value">{formatMinutes(summary?.avg_response_minutes ?? null)}</div>
        </div>
        <div className="summary-card">
          <h3>Avg Resolution Time</h3>
          <div className="summary-value">{formatMinutes(summary?.avg_resolution_minutes ?? null)}</div>
        </div>
      </div>

      {/* Alert Response Time Trend */}
      <div className="reports-grid">
        <div className="chart-section">
          <div className="portlet">
            <div className="portlet-body">
              <h2>Alert Volume & Resolution (Weekly)</h2>
              {weeklyTrend.length > 0 ? (
                <div className="line-chart">
                  <div className="chart-container">
                    <BarChart
                      data={weeklyTrend.map(w => ({
                        label: w.week_start ? new Date(w.week_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : w.week_key,
                        values: [w.alert_count, w.resolved_count]
                      }))}
                      colors={['#dc3545', '#28a745']}
                      legendLabels={['Alerts Created', 'Resolved']}
                    />
                  </div>
                </div>
              ) : (
                <p className="no-data">No alert data for this period</p>
              )}
            </div>
          </div>
        </div>

        <div className="chart-section">
          <div className="portlet">
            <div className="portlet-body">
              <h2>Developer Response Time</h2>
              {devResponseData.length > 0 ? (
                <div className="line-chart">
                  <div className="chart-container">
                    <HorizontalBarChart
                      data={devResponseData.map(d => ({
                        label: d.developer_name,
                        value: d.avg_resolution_minutes ?? 0
                      }))}
                      color="#007bff"
                      formatValue={formatMinutes}
                    />
                  </div>
                </div>
              ) : (
                <p className="no-data">No developer resolution data available</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Requests per Developer */}
      <div className="team-performance-section">
        <div className="portlet">
          <div className="portlet-body">
            <h2>Requests per Developer</h2>
            {requestsPerDev.length > 0 ? (
              <div className="performance-table">
                <table>
                  <thead>
                    <tr>
                      <th>Developer</th>
                      <th>Total</th>
                      <th>Completed</th>
                      <th>In Progress</th>
                      <th>Open</th>
                      <th>Avg Resolution</th>
                      <th>Workload</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requestsPerDev.map((dev, idx) => {
                      const maxRequests = Math.max(...requestsPerDev.map(d => d.total_requests));
                      const barWidth = maxRequests > 0 ? (dev.total_requests / maxRequests) * 100 : 0;
                      return (
                        <tr key={idx}>
                          <td className="team-name">{dev.developer_name}</td>
                          <td>{dev.total_requests}</td>
                          <td>{dev.completed_requests}</td>
                          <td>{dev.in_progress_requests}</td>
                          <td>{dev.open_requests}</td>
                          <td>{dev.avg_resolution_hours ? `${Math.round(dev.avg_resolution_hours)}h` : '—'}</td>
                          <td>
                            <div className="performance-bar">
                              <div
                                className="performance-fill"
                                style={{ width: `${barWidth}%`, backgroundColor: '#007bff' }}
                              ></div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="no-data">No request data for this period</p>
            )}
          </div>
        </div>
      </div>

      {/* Automations per Developer & Requests per Automation side by side */}
      <div className="reports-grid">
        <div className="chart-section">
          <div className="portlet">
            <div className="portlet-body">
              <h2>Automations per Developer</h2>
              {automationsPerDev.length > 0 ? (
                <div className="line-chart">
                  <div className="chart-container">
                    <HorizontalBarChart
                      data={automationsPerDev.map(d => ({
                        label: d.developer_name,
                        value: d.automation_count
                      }))}
                      color="#6f42c1"
                      formatValue={(v) => String(Math.round(v))}
                    />
                  </div>
                </div>
              ) : (
                <p className="no-data">No contribution data available</p>
              )}
            </div>
          </div>
        </div>

        <div className="chart-section">
          <div className="portlet">
            <div className="portlet-body">
              <h2>Requests per Automation</h2>
              {requestsPerAuto.length > 0 ? (
                <div className="line-chart">
                  <div className="chart-container">
                    <HorizontalBarChart
                      data={requestsPerAuto.slice(0, 10).map(d => ({
                        label: d.automation_name,
                        value: d.total_requests
                      }))}
                      color="#20c997"
                      formatValue={(v) => String(Math.round(v))}
                    />
                  </div>
                </div>
              ) : (
                <p className="no-data">No request data for this period</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Requests per Automation Detail Table */}
      <div className="team-performance-section">
        <div className="portlet">
          <div className="portlet-body">
            <h2>Requests per Automation (Detail)</h2>
            {requestsPerAuto.length > 0 ? (
              <div className="performance-table">
                <table>
                  <thead>
                    <tr>
                      <th>Automation</th>
                      <th>Total</th>
                      <th>Bugs</th>
                      <th>Features</th>
                      <th>Improvements</th>
                      <th>Completed</th>
                      <th>Open</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requestsPerAuto.map((auto, idx) => (
                      <tr key={idx}>
                        <td className="team-name">{auto.automation_name}</td>
                        <td>{auto.total_requests}</td>
                        <td>{auto.bug_count}</td>
                        <td>{auto.feature_count}</td>
                        <td>{auto.improvement_count}</td>
                        <td>{auto.completed_requests}</td>
                        <td>{auto.open_requests}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="no-data">No request data for this period</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;
